/**
 * Tenant Switcher Component
 * Allows platform admins to switch context to manage other tenants
 */

import React, { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { Organization } from '../../types/tenant';


interface TenantSwitcherProps {
  accessToken: string;
  apiBaseUrl: string;
}

const TenantSwitcher: React.FC<TenantSwitcherProps> = ({ accessToken, apiBaseUrl }) => {
  const { switchToTenant } = useTenant();
  const [allTenants, setAllTenants] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');

  useEffect(() => {
    loadAllTenants();
  }, []);

  const loadAllTenants = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/tenant-admin/list`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAllTenants(data.organizations || []);
      } else {
        throw new Error(`Failed to load tenants: ${response.statusText}`);
      }
    } catch (err: any) {
      console.error('Error loading tenants:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchTenant = () => {
    if (!selectedTenantId) return;

    const tenant = allTenants.find(t => t.tenantId === selectedTenantId);
    if (tenant) {
      switchToTenant(tenant);
    }
  };

  return (
    <div className="bg-surface-subtle border border-gray-200 rounded-lg p-5 mb-6">
      <h3 className="text-body font-semibold text-gray-900 mb-1">🔧 Manage Another Tenant</h3>
      <p className="text-label text-gray-500 mb-4">Switch context to manage workflows and settings for another tenant.</p>

      {error && (
        <div className="mb-4 px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label">
          ❌ {error}
        </div>
      )}

      <div className="flex gap-3 items-center">
        <select aria-label="Select tenant to manage" value={selectedTenantId}
          onChange={(e) => setSelectedTenantId(e.target.value)}
          disabled={isLoading}
          className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast select-input disabled:bg-gray-100">
          <option value="">Select a tenant…</option>
          {allTenants.map(tenant => (
            <option key={tenant.tenantId} value={tenant.tenantId}>{tenant.name} ({tenant.domain})</option>
          ))}
        </select>
        <button type="button" onClick={handleSwitchTenant} disabled={!selectedTenantId || isLoading}
          className="px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast shadow-sm disabled:opacity-50 whitespace-nowrap">
          Switch to Tenant
        </button>
      </div>

      {isLoading && <p className="mt-3 text-label text-gray-400">Loading tenants…</p>}
    </div>
  );
};

export default TenantSwitcher;

